import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Box, Button, IconButton, useTheme, useMediaQuery } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import MobileOptionDrawer from '../../../../Overview/MobileOptionDrawer';
import { getExchangeCode } from '../../../../../services/exchanges';
import { getInternationalCardStyles } from './InternationalCardCss';

// Import new modular hooks and components
import { useLivePrice } from '../hooks/useLivePrice';
import { useOrderActions } from '../hooks/useOrderActions';
import InternationalCardUI from './InternationalCardUI';

const InternationalCard = ({ instrument }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const styles = useMemo(() => getInternationalCardStyles(theme, isDark), [theme, isDark]);

  // Desktop hover state
  const [isHovered, setIsHovered] = useState(false);
  const leaveTimerRef = useRef(null);

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Derived values
  const isTradable = instrument.tradable === true && !instrument.index;
  const wsCode = useMemo(() => {
    try {
      return getExchangeCode(instrument.exchange);
    } catch {
      return null;
    }
  }, [instrument.exchange]);

  // Hook 1: Manage WebSocket live price
  const { price, change } = useLivePrice(instrument.exchange, instrument.token, wsCode);

  // Hook 2: Manage Redux dispatching for orders/charts
  const { handleBuy, handleSell, handleChart } = useOrderActions(instrument, isTradable, price);

  // Desktop hover handlers
  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    setIsHovered(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => setIsHovered(false), 0);
  }, []);

  // Mobile card tap
  const handleCardClick = useCallback(() => {
    if (!isMobile || !isTradable) return;
    setDrawerOpen(true);
  }, [isMobile, isTradable]);

  // Mobile drawer action handlers
  const handleDrawerBuy = useCallback(() => {
    handleBuy();
    setDrawerOpen(false);
  }, [handleBuy]);

  const handleDrawerSell = useCallback(() => {
    handleSell();
    setDrawerOpen(false);
  }, [handleSell]);

  const handleDrawerChart = useCallback(() => {
    handleChart();
    setDrawerOpen(false);
  }, [handleChart]);

  return (
    <Box sx={styles.wrapper}>
      <InternationalCardUI
        name={instrument.display_name}
        price={price}
        change={change}
        pricePrecision={instrument.price_precision ?? 2}
        onClick={isMobile && isTradable ? handleCardClick : null}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {/* Desktop hover pill — Buy / Sell / Chart */}
      {!isMobile && isHovered && isTradable && (
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => e.stopPropagation()}
          sx={styles.actionPill}
        >
          <Button size="small" variant="contained" onClick={handleBuy} sx={styles.buyBtn}>
            B
          </Button>
          <Button size="small" variant="contained" onClick={handleSell} sx={styles.sellBtn}>
            S
          </Button>
          <IconButton size="small" onClick={handleChart} sx={styles.chartBtn}>
            <TimelineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* Mobile bottom drawer */}
      {isMobile && (
        <MobileOptionDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          instrument={{
            display_name: instrument.display_name,
            trading_symbol: instrument.trading_symbol,
            exchange: instrument.exchange,
            ltp: price,
            tradable: isTradable,
          }}
          onBuyClick={handleDrawerBuy}
          onSellClick={handleDrawerSell}
          onChartClick={handleDrawerChart}
        />
      )}
    </Box>
  );
};

export default InternationalCard;
